import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../api/client';
import { formatMkd } from '../lib/format';
import LineChart from '../components/charts/LineChart';
import BarChart from '../components/charts/BarChart';
import { MEASURE_COLOR, BUSINESS_COLOR, DELTA } from '../components/charts/palette';

/**
 * Paneli i kontrollit. Ports frmDashboard and FitimetShpenzimetDashboard.
 *
 * "Shpenzimet" here means expenses plus salaries, as the desktop computed it.
 *
 * The scope tabs are the desktop's four, and "Stargroup" means what it means
 * there: all four businesses combined, not biznesiID 4 on its own
 * (frmDashboard.cs:75 swaps the filter for `1=1`). It is the tab the screen
 * opens on, as frmDashboard_Load does.
 */

interface Totals {
  fitimet: number;
  shpenzimet: number;
  profit: number;
}

interface DashboardData {
  scopes: { slug: string; name: string; combined: boolean }[];
  summary: {
    thisMonth: Totals;
    lastMonth: Totals;
    change: Totals;
    percentChange: { fitimet: number | null; shpenzimet: number | null };
  };
  trend: { year: number; month: number; monthName: string; fitimet: number; shpenzimet: number }[];
  spendByBusiness: {
    year: number;
    month: number;
    monthName: string;
    values: Record<string, number>;
  }[];
}

const BUSINESS_LABELS: Record<string, string> = {
  stargym: 'Stargym',
  missfit: 'Missfit',
  starbar: 'Starbar',
  stargroup: 'Stargroup',
};

export default function Dashboard() {
  const [scope, setScope] = useState('stargroup');
  const [months, setMonths] = useState(12);

  const dashboardQuery = useQuery({
    queryKey: ['dashboard', scope, months],
    queryFn: () => apiGet<DashboardData>('/dashboard', { biznesi: scope, months }),
    placeholderData: (prev) => prev,
  });

  const data = dashboardQuery.data;
  const summary = data?.summary;
  const scopeIsCombined = data?.scopes.find((s) => s.slug === scope)?.combined ?? false;
  const trendLabels = (data?.trend ?? []).map((p) => `${p.monthName.slice(0, 3)} ${p.year}`);
  const spendLabels = (data?.spendByBusiness ?? []).map((p) => `${p.monthName.slice(0, 3)} ${p.year}`);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Paneli i kontrollit</h1>
          <p className="text-sm text-muted">
            Fitimet, shpenzimet dhe profiti për muajin aktual
            {scopeIsCombined ? ' — të katër bizneset së bashku' : ''}
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <div>
            <label className="mb-1 block text-xs font-medium text-muted" htmlFor="dash-scope">
              Biznesi
            </label>
            <select
              id="dash-scope"
              value={scope}
              onChange={(e) => setScope(e.target.value)}
              className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
            >
              {(data?.scopes ?? []).map((s) => (
                <option key={s.slug} value={s.slug}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-muted" htmlFor="dash-months">
              Periudha
            </label>
            <select
              id="dash-months"
              value={months}
              onChange={(e) => setMonths(Number(e.target.value))}
              className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
            >
              <option value={6}>6 muajt e fundit</option>
              <option value={12}>12 muajt e fundit</option>
              <option value={24}>24 muajt e fundit</option>
            </select>
          </div>
        </div>
      </div>

      {/* Headline figures. Not charts — a single number each, so a stat tile. */}
      <div className="grid gap-3 sm:grid-cols-3">
        <StatTile
          label="Fitimet (ky muaj)"
          value={summary?.thisMonth.fitimet ?? 0}
          change={summary?.change.fitimet ?? 0}
          percent={summary?.percentChange.fitimet ?? null}
          accent={MEASURE_COLOR.fitimet}
          moreIsBetter
        />
        <StatTile
          label="Shpenzimet (ky muaj)"
          value={summary?.thisMonth.shpenzimet ?? 0}
          change={summary?.change.shpenzimet ?? 0}
          percent={summary?.percentChange.shpenzimet ?? null}
          accent={MEASURE_COLOR.shpenzimet}
          moreIsBetter={false}
        />
        <StatTile
          label="Profiti (ky muaj)"
          value={summary?.thisMonth.profit ?? 0}
          change={summary?.change.profit ?? 0}
          percent={null}
          moreIsBetter
        />
      </div>

      <section className="rounded-xl bg-white p-4">
        <h2 className="mb-1 font-semibold">Fitimet kundrejt shpenzimeve</h2>
        <p className="mb-3 text-sm text-muted">
          Shpenzimet përfshijnë edhe rrogat
        </p>
        <LineChart
          ariaLabel="Fitimet dhe shpenzimet sipas muajve"
          labels={trendLabels}
          series={[
            {
              key: 'fitimet',
              label: 'Fitimet',
              color: MEASURE_COLOR.fitimet,
              values: (data?.trend ?? []).map((p) => p.fitimet),
            },
            {
              key: 'shpenzimet',
              label: 'Shpenzimet',
              color: MEASURE_COLOR.shpenzimet,
              values: (data?.trend ?? []).map((p) => p.shpenzimet),
            },
          ]}
        />
      </section>

      <section className="rounded-xl bg-white p-4">
        <h2 className="mb-1 font-semibold">Shpenzimet sipas biznesit</h2>
        <p className="mb-3 text-sm text-muted">Të gjitha bizneset, pavarësisht filtrit më lart</p>
        <BarChart
          ariaLabel="Shpenzimet sipas biznesit dhe muajit"
          labels={spendLabels}
          series={Object.keys(BUSINESS_LABELS).map((slug) => ({
            key: slug,
            label: BUSINESS_LABELS[slug]!,
            color: BUSINESS_COLOR[slug]!,
            values: (data?.spendByBusiness ?? []).map((p) => p.values[slug] ?? 0),
          }))}
        />
      </section>

      {/* Table view — the relief obligation for the sub-3:1 series colour, and
          the thing anyone reading exact figures actually wants. */}
      <section className="overflow-hidden rounded-xl bg-white">
        <div className="border-b border-slate-200 px-4 py-3">
          <h2 className="font-semibold">Të dhënat</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[520px] text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-muted">
                <th className="px-4 py-2.5 font-semibold">Muaji</th>
                <th className="px-4 py-2.5 text-right font-semibold">Fitimet</th>
                <th className="px-4 py-2.5 text-right font-semibold">Shpenzimet</th>
                <th className="px-4 py-2.5 text-right font-semibold">Profiti</th>
              </tr>
            </thead>
            <tbody>
              {(data?.trend ?? [])
                .slice()
                .reverse()
                .map((p) => {
                  const profit = p.fitimet - p.shpenzimet;
                  return (
                    <tr key={`${p.year}-${p.month}`} className="border-b border-slate-100 last:border-0">
                      <td className="px-4 py-2">
                        {p.monthName} {p.year}
                      </td>
                      <td className="tnum px-4 py-2 text-right">{formatMkd(p.fitimet)}</td>
                      <td className="tnum px-4 py-2 text-right">{formatMkd(p.shpenzimet)}</td>
                      <td
                        className="tnum px-4 py-2 text-right font-medium"
                        style={{ color: profit >= 0 ? DELTA.good : DELTA.bad }}
                      >
                        {formatMkd(profit)}
                      </td>
                    </tr>
                  );
                })}
              {(data?.trend ?? []).length === 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-10 text-center text-muted">
                    Nuk ka të dhëna për këtë periudhë.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function StatTile({
  label,
  value,
  change,
  percent,
  accent,
  moreIsBetter,
}: {
  label: string;
  value: number;
  change: number;
  percent: number | null;
  accent?: string;
  moreIsBetter: boolean;
}) {
  // Spending more is bad even though the number went up, so "good" depends on
  // the measure rather than the sign — same convention the desktop used.
  const isGood = moreIsBetter ? change >= 0 : change <= 0;
  const color = change === 0 ? undefined : isGood ? DELTA.good : DELTA.bad;

  return (
    <div className="rounded-xl bg-white p-4">
      <div className="flex items-center gap-2">
        {accent && (
          <span
            className="inline-block h-2.5 w-2.5 rounded-full"
            style={{ background: accent }}
            aria-hidden="true"
          />
        )}
        <span className="text-xs font-medium text-muted">{label}</span>
      </div>

      <div className="mt-1 text-2xl font-semibold">{formatMkd(value)}</div>

      <div className="mt-1 flex items-center gap-2 text-xs">
        <span style={{ color }} className="tnum font-medium">
          {change >= 0 ? '+' : ''}
          {formatMkd(change)}
        </span>
        {percent !== null && (
          <span style={{ color }} className="tnum">
            ({percent >= 0 ? '+' : ''}
            {percent.toFixed(1)}%)
          </span>
        )}
        <span className="text-muted">vs muaji i kaluar</span>
      </div>
    </div>
  );
}
